'use client';

import { useEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode;
  /** Toggle state. Sets `aria-pressed`; leave undefined for a plain button (a sheet opener). */
  selected?: boolean;
  /** Paints the selected look without claiming a toggle (the Filters opener with filters applied). */
  highlight?: boolean;
  /** Leading icon; decorative, give it `aria-hidden`. */
  icon?: ReactNode;
  /** Trailing count (applied filters, results). */
  count?: number;
  /** Removable variant: shows a trailing "×" and uses this as the accessible name. */
  removeLabel?: string;
  onRemove?: () => void;
  /** Show the label as given instead of upper-casing it (data such as a division name). */
  preserveCase?: boolean;
}

/**
 * Compact toggle / filter chip. Paints 2rem tall; a centred invisible `before:` band lifts the tap
 * target to 44px. State is never colour alone: a selected chip also takes `aria-pressed`, and an
 * applied chip carries its "×".
 */
export function Chip({
  children,
  selected,
  highlight,
  icon,
  count,
  removeLabel,
  onRemove,
  preserveCase,
  onClick,
  className,
  type = 'button',
  ...rest
}: ChipProps) {
  const removable = removeLabel !== undefined;
  const on = selected === true || highlight === true || removable;
  return (
    <button
      type={type}
      aria-pressed={removable ? undefined : selected}
      aria-label={removable ? removeLabel : undefined}
      onClick={removable ? () => onRemove?.() : onClick}
      className={cn(
        "focus-visible:ring-ring relative inline-flex min-h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 font-mono text-[0.6875rem] font-semibold tracking-[0.08em] whitespace-nowrap transition-colors before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-[''] focus-visible:ring-2 focus-visible:outline-none",
        !preserveCase && 'uppercase',
        on
          ? 'border-accent bg-accent/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-surface',
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
      {count !== undefined && count > 0 && <span>{count}</span>}
      {removable && <span aria-hidden="true">×</span>}
    </button>
  );
}

/**
 * One flat line of chips: scrolls sideways, never wraps. The edge gutter bleeds to the page
 * padding (12px on phones, 16px from `md`) so chips slide under the screen edge, not a clipped box.
 * `activeKey` scrolls the pressed chip into view whenever it changes.
 */
export function ChipRow({
  label,
  children,
  className,
  activeKey,
}: {
  label?: string;
  children: ReactNode;
  className?: string;
  activeKey?: string | number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (activeKey === undefined) return;
    const active = ref.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
    active?.scrollIntoView?.({ inline: 'center', block: 'nearest' });
  }, [activeKey]);

  return (
    <div
      ref={ref}
      role="group"
      aria-label={label}
      className={cn(
        // py-1.5 holds each chip's 44px tap band (6px past a 2rem chip, top and bottom) inside the
        // scroller and -my-1.5 hands that space back; overflow-x-auto alone turns overflow-y to
        // auto, so the band would otherwise make the row scroll 6px up and down too.
        '-mx-3 -my-1.5 flex gap-2 overflow-x-auto overflow-y-hidden px-3 py-1.5 [scrollbar-width:none] md:-mx-4 md:px-4 [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {children}
    </div>
  );
}
